import { Modal } from "antd";
import {
  BgColorsOutlined,
  CheckOutlined,
  MoonOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ThemeMode } from "../../api/configuration";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setThemeMode } from "../../store/themeSlice";
import {
  THEME_COLORS,
  themeColorLabelKey,
  themeModeOf,
  themePaletteOf,
} from "../../lib/themePalettes";
import type { ThemePalette } from "../../lib/themePalettes";
import { saveDeviceTheme } from "../../lib/theme";
import { modalStyles, modalWrapStyle } from "../../utils/modalStyles";

// „Тема“ — цвят + светла/тъмна (профилното меню и бутонът на ПИН екрана).
// Темата е на компютъра (lib/theme.ts) — изборът се пази в localStorage и
// важи за всеки, който влиза на това устройство, и на входа.
// Горе превключвателят „Светла | Тъмна“ (сменя веднага, модалът остава
// отворен), отдолу цветовете — всяка карта е мостра на СВОЯ цвят във
// варианта от превключвателя, по модела на демото „Цветови теми“: маркирана
// маса, обикновена, угаснала и трите бутона на лентата. Цветовете в мострата
// са на чуждата тема, не на текущата, затова са inline style от
// lib/themePalettes.ts (стойности по тема, не статичен стил).
export default function ThemeModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentTheme = useAppSelector((state) => state.theme.mode);
  const current = themePaletteOf(currentTheme);
  const apply = (mode: ThemeMode) => {
    // Записва се и изричният избор на текущата — на компютър без записана тема
    // иначе UserConfigurationSync би я попълнил от базата при следващия вход.
    saveDeviceTheme(mode);
    if (mode !== currentTheme) dispatch(setThemeMode(mode));
  };

  const brightnessOptions = [
    { isDark: false, icon: <SunOutlined />, label: t("common.themeLight") },
    { isDark: true, icon: <MoonOutlined />, label: t("common.themeDark") },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={500}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="flex items-center gap-2.5 px-5 pt-[18px] pb-3.5">
        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-brand-tint text-[16px] text-brand-ink">
          <BgColorsOutlined />
        </div>
        <div className="text-[15px] leading-[1.2] font-bold tracking-[-0.2px] text-ink">
          {t("common.chooseTheme")}
        </div>
      </div>

      <div className="h-px bg-shade-2" />

      <div className="px-3 pt-3">
        <div className="grid grid-cols-2 gap-[3px] rounded-[11px] bg-fill-2 p-[3px]">
          {brightnessOptions.map((option) => {
            const selected = current.isDark === option.isDark;
            return (
              <button
                key={option.label}
                type="button"
                aria-pressed={selected}
                onClick={() => apply(themeModeOf(current.color, option.isDark))}
                className={`flex h-9 cursor-pointer items-center justify-center gap-2 rounded-[8px] border-none text-[13px] font-semibold transition-colors duration-[120ms] ${
                  selected
                    ? "bg-surface text-ink shadow-[0_1px_2px_rgba(17,17,17,0.12)]"
                    : "bg-transparent text-soft hover:text-ink"
                }`}
              >
                {option.icon}
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 p-3">
        {THEME_COLORS.map((color) => {
          const palette = themePaletteOf(themeModeOf(color, current.isDark));
          return (
            <ThemeCard
              key={color}
              palette={palette}
              label={t(themeColorLabelKey(color))}
              active={current.color === color}
              onClick={() => {
                apply(palette.mode);
                onClose();
              }}
            />
          );
        })}
      </div>
    </Modal>
  );
}

function ThemeCard({
  palette,
  label,
  active,
  onClick,
}: {
  palette: ThemePalette;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const tile = "flex min-w-0 flex-col gap-1 rounded-[7px] px-1.5 py-1";
  // Трите бутона на лентата (Нова маса · Добави · Закрий) като цветни ленти.
  const button = "h-[16px] min-w-0 flex-1 rounded-[5px]";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex min-w-0 cursor-pointer flex-col gap-2 rounded-[12px] border bg-surface p-2 text-left transition-colors duration-[120ms] ${
        active
          ? "border-brand shadow-[0_0_0_1px_var(--color-brand)]"
          : "border-shade-2 hover:border-shade-4"
      }`}
    >
      <div
        className="flex flex-col gap-1.5 rounded-[9px] p-1.5"
        style={{ background: palette.canvas }}
      >
        <div className="grid grid-cols-3 gap-1">
          <span
            className={`${tile} border-[1.5px]`}
            style={{ background: palette.tint, borderColor: palette.accent }}
          >
            <span
              className="text-[11px] leading-none font-semibold"
              style={{ color: palette.ink }}
            >
              1
            </span>
            <span
              className="text-right font-pos-mono text-[9px] leading-none"
              style={{ color: palette.ink }}
            >
              7.00
            </span>
          </span>
          <span
            className={`${tile} border`}
            style={{ background: palette.surface, borderColor: palette.border }}
          >
            <span
              className="text-[11px] leading-none font-semibold"
              style={{ color: palette.accentText }}
            >
              5
            </span>
            <span
              className="text-right font-pos-mono text-[9px] leading-none"
              style={{ color: palette.ink }}
            >
              39.00
            </span>
          </span>
          <span
            className={`${tile} border`}
            style={{ background: palette.inactive, borderColor: palette.border }}
          >
            <span
              className="text-[11px] leading-none font-semibold"
              style={{ color: palette.muted }}
            >
              14
            </span>
            <span
              className="text-right font-pos-mono text-[9px] leading-none"
              style={{ color: palette.muted }}
            >
              23.40
            </span>
          </span>
        </div>
        <div className="flex gap-1">
          <span
            className={`${button} border`}
            style={{ background: palette.surface, borderColor: palette.border }}
          />
          <span className={button} style={{ background: palette.accent }} />
          <span
            className={`${button} border`}
            style={{ background: palette.tint, borderColor: palette.tintBorder }}
          />
        </div>
      </div>
      <div className="flex min-w-0 items-center justify-between gap-2 px-1">
        <span className="truncate text-[13px] leading-[1.2] font-semibold text-ink">
          {label}
        </span>
        {active && <CheckOutlined className="text-[12px] text-brand-ink" />}
      </div>
    </button>
  );
}
